/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

export const CDS_COMMAND_INTELLIGENCE_DRAWER_KEY_PREFIX = 'cds-ci-drawer:';

export const COMMAND_INTELLIGENCE_GLOBAL_PREFS_SCOPE_ID = 'app';

export const LEGACY_COMMAND_INTELLIGENCE_DRAWER_WIDTH_KEY = 'commandIntelligenceDrawerWidth';

/**
 * Stable per-user segment for drawer preference keys.
 *
 * @param {object | null | undefined} user
 * @returns {string}
 */
export const getCommandIntelligenceUserIdentityKey = user => {
  return user?.email || user?.id || user?.username || user?.profileRef || 'anon';
};

/**
 * Normalizes registration scope input into a stable scope segment.
 *
 * @param {string | undefined} widthCacheKey
 * @returns {string}
 */
export const normalizeCommandIntelligencePrefsScopeId = widthCacheKey => {
  if (!widthCacheKey || widthCacheKey === LEGACY_COMMAND_INTELLIGENCE_DRAWER_WIDTH_KEY) {
    return 'app';
  }

  return widthCacheKey;
};

/**
 * Builds the localStorage prefix for Command Intelligence drawer preferences.
 * Preferences are scoped by entity, user, and optional screen scope.
 *
 * @param {{ entityRef?: string, userIdentityKey?: string, scopeId?: string }} params
 * @returns {string}
 */
export const buildCommandIntelligenceWidthCacheKey = ({ entityRef, userIdentityKey, scopeId } = {}) => {
  const entity = entityRef || 'global';
  const user = userIdentityKey || 'anon';
  const scope = normalizeCommandIntelligencePrefsScopeId(scopeId);

  return `${CDS_COMMAND_INTELLIGENCE_DRAWER_KEY_PREFIX}${entity}:${user}:${scope}`;
};

/**
 * Parses a resolved drawer prefs cache key into its entity, user, and scope segments.
 *
 * @param {string | undefined} widthCacheKey
 * @returns {{ entityRef: string, userIdentityKey: string, scopeId: string } | null}
 */
export const parseCommandIntelligenceDrawerPrefsCacheKey = widthCacheKey => {
  if (!widthCacheKey?.startsWith?.(CDS_COMMAND_INTELLIGENCE_DRAWER_KEY_PREFIX)) {
    return null;
  }

  const remainder = widthCacheKey.slice(CDS_COMMAND_INTELLIGENCE_DRAWER_KEY_PREFIX.length);
  const segments = remainder.split(':');
  const entityRef = segments?.[0];
  const userIdentityKey = segments?.[1];
  const scopeId = segments?.slice?.(2)?.join?.(':');

  if (!entityRef || !userIdentityKey || !scopeId) {
    return null;
  }

  return { entityRef, userIdentityKey, scopeId };
};

/**
 * Resolves the shared drawer prefs cache key for the active entity and user.
 * Layout, size, dock, and open preferences use one app-wide scope across all pages.
 *
 * @param {{
 *   entityRef?: string,
 *   userIdentityKey?: string,
 *   prefsScopeId?: string,
 *   widthCacheKey?: string,
 * }} params
 * @returns {string}
 */
export const resolveCommandIntelligenceDrawerPrefsCacheKey = ({ entityRef, userIdentityKey } = {}) => {
  return buildCommandIntelligenceWidthCacheKey({
    entityRef,
    userIdentityKey,
    scopeId: COMMAND_INTELLIGENCE_GLOBAL_PREFS_SCOPE_ID,
  });
};
